import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'Do you offer same-day leak detection?',
    answer: 'Yes — we offer same-day service throughout Las Vegas and surrounding areas. When you call, we respond quickly and get to work locating the source of the leak.',
  },
  {
    question: 'Will you have to break walls or concrete?',
    answer: 'No — we specialize in non-invasive leak detection. Our goal is to locate the leak without unnecessary damage. If access is ever required, we\'ll explain everything before moving forward.',
  },
  {
    question: 'How long does leak detection take?',
    answer: 'Every leak is different. Some are straightforward, while others require more detailed testing. We use flat-rate pricing, so your cost does not change based on time.',
  },
  {
    question: 'Do I need to know where the leak is before calling?',
    answer: 'No — that\'s exactly what we\'re here for. We use professional equipment to pinpoint the source of the leak accurately.',
  },
  {
    question: 'Do you work with insurance claims?',
    answer: 'Yes — we can assist with documentation and help guide you through the process if your leak may be covered by insurance.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 sm:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-block bg-cyan-50 text-cyan-700 px-4 py-2 rounded-full text-sm font-bold mb-4 border border-cyan-200">
            Common Questions
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0f1f3d] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Have a question not listed here?{' '}
            <a href="tel:7252675482" className="text-red-600 font-bold hover:text-red-700 underline underline-offset-2">
              Call us now
            </a>{' '}
            — we're happy to help.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                aria-expanded={openIndex === index}
              >
                <span className="text-base sm:text-lg font-bold text-[#0f1f3d] leading-snug">
                  {faq.question}
                </span>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-cyan-500 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5 pt-1 bg-gray-50 border-t border-gray-100">
                  <p className="text-base text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-[#0a1628] to-[#162447] rounded-2xl p-8">
          <p className="text-white font-bold text-lg mb-4">Ready to find your leak? We're standing by.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:7252675482"
              className="w-full sm:w-auto bg-red-600 text-white px-8 py-4 rounded-lg font-black text-lg hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-phone-fill"></i>
              Call Now
            </a>
            <a
              href="#contact"
              className="w-full sm:w-auto border-2 border-white/40 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              Get Free Estimate
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
